import { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider } from '@xmanscript/useform';
import defaultDp from '@/assets/image/default-dp.png';
import Button from '../Button';
import ImageUpload from '../ImageUpload';
import NameEdit from '../NameEdit';
import CategoryEdit from '../CategoryEdit';
import ToggleWithLabel from '../ToggleWithLabel';
import { useUser } from '@/contexts/UserContext/UserContext';
import { ReactComponent as Logout } from '@/assets/image/logout.svg';
import { UserService } from '@/ui/_apiUrls';
import { authenticatedApi } from '@/api/config';
import ErrorLabel from '../FormControl/_lib/ErrorLabel';
import useAuth from '@/hooks/useAuth';

export default function ProfileTab() {
  const { user, updateUser, refreshUser } = useUser();
  const { logOut } = useAuth();
  const [isEditingName, setIsEditingName] = useState(false);

  // Define logout handler early
  const handleLogOutClick = () => {
    logOut();
  };

  // Keep a ref to store the current values and functions
  const currentFormValuesRef = useRef<any>(null);
  const currentSetValuesRef = useRef<any>(null);

  const { mutate: patchUser, isSuccess: isPatchUserSuccess, isError: isPatchUserError } = UserService.patch();

  // Define the form first so we have setValues available
  const { register, values, onSubmitHandler, formState, setValues } = useForm({
    formName: 'profile-form',
    initialValues: user ? JSON.parse(JSON.stringify(user)) : {},
    persistValues: false, // Disable persistence to avoid FormProvider errors
    touchOnChange: false, // Disable touch on change to avoid console warnings
    validationSchema: (vals: Record<string, any>) => {
      const errors: Record<string, any> = {};
      if (!vals) return errors;

      const { name } = vals;
      if (!name || (typeof name === 'string' && !name.trim())) {
        errors.name = 'Name is required.';
      }
      // Categories validation handled in drawer
      return errors;
    },
    submitHandler: async (vals) => {
      try {
        // Extract values from the submission data
        const formDataValues = vals.currentPacket || vals;

        const { image = null, showProfileName = undefined, name = undefined, categories = undefined } = formDataValues;

        // Use the current form values from ref instead of the submitted values for categories
        const currentCategories = currentFormValuesRef.current?.categories || values?.categories || categories;

        // Ensure categories is always an array
        let categoriesArray: any[] = [];
        if (Array.isArray(currentCategories)) {
          categoriesArray = currentCategories;
        } else if (typeof currentCategories === 'string') {
          categoriesArray = [currentCategories];
        } else if (currentCategories) {
          categoriesArray = [currentCategories];
        }

        if (image && Array.isArray(image) && image.length > 0) {
          const formData = new FormData();
          formData.append('file', image[0]);

          try {
            // Step 1: Upload the media file to get mediaId
            const mediaResponse = await authenticatedApi.post('/api/v1/media/', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            // Step 2: Use the mediaId to set the profile picture
            if (mediaResponse.data && mediaResponse.data.id) {
              await authenticatedApi.post('/api/v1/user/upload-profile-picture/', {
                mediaId: mediaResponse.data.id,
              });
              
              // Refresh the user context to get the updated thumbnail
              setTimeout(() => {
                // Use the refreshUser function from the UserContext
                if (typeof refreshUser === 'function') {
                  refreshUser();
                }
              }, 100); // Small delay to ensure the backend operation completes
            }

            // Also update other fields if they exist
            const updateData: any = { profileComplete: true };
            if (showProfileName !== undefined) updateData.showProfileName = showProfileName;
            if (name !== undefined) updateData.name = name;
            // Always send categories as an array
            updateData.categories = categoriesArray;

            await patchUser(updateData);
          } catch (error) {
            console.error('Error uploading profile picture:', error);
            throw error;
          }
        } else {
          // Update all fields that exist - make sure categories are definitely included
          const updateData: any = { profileComplete: true };
          if (showProfileName !== undefined) updateData.showProfileName = showProfileName;
          if (name !== undefined) updateData.name = name;
          // Always send categories as an array
          updateData.categories = categoriesArray;

          await patchUser(updateData);
        }
      } catch (error: any) {
        throw new Error(error?.message || 'Form submission failed');
      }
    },
  });

  // Update the ref when values and setValues are available
  useEffect(() => {
    currentFormValuesRef.current = values;
    currentSetValuesRef.current = setValues;
  }, [values, setValues]);

  // Disabled auto-submit to prevent infinite API calls
  // useEffect(() => {
  //   let timer: NodeJS.Timeout | undefined;
  //   // Auto-submit logic disabled
  //   return () => {
  //     if (timer) {
  //       clearTimeout(timer);
  //     }
  //   };
  // }, []);

  useEffect(() => {
    if (isPatchUserSuccess && values) {
      const { image, ...restData } = values;
      // Make sure to include the current thumbnail when updating other fields
      // Update the user context with all the latest values, including categories
      updateUser({
        ...restData,
        thumbnail: values?.thumbnail || user?.thumbnail,
      });
    }
  }, [isPatchUserSuccess, updateUser, values, user?.thumbnail]);

  useEffect(() => {
    const handleCategorySubmit = () => {
      onSubmitHandler();
    };

    window.addEventListener('categorySubmit', handleCategorySubmit);
    return () => {
      window.removeEventListener('categorySubmit', handleCategorySubmit);
    };
  }, [onSubmitHandler]);

  // Handle category changes manually
  useEffect(() => {
    const handleCategoryChange = (event: CustomEvent) => {
      const { categories } = event.detail;
      if (typeof setValues === 'function') {
        setValues((prev: any) => ({
          ...prev,
          categories,
        }));
      }
    };

    window.addEventListener('categoryChange', handleCategoryChange as EventListener);
    return () => {
      window.removeEventListener('categoryChange', handleCategoryChange as EventListener);
    };
  }, [setValues]);

  // If no user data, show a loading or empty state
  if (!user) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Loading user profile...</p>
          <Button onClick={handleLogOutClick} className="mt-4 flex w-fit items-center gap-3" variant="primary">
            <Logout />
            <p>Log Out</p>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...{ register, values, onSubmitHandler, formState, setValues }}>
      <div className="flex h-full w-full flex-col justify-between pt-1">
        <div className="form-controls flex flex-1 flex-col gap-6">
          <div className="top flex flex-col items-center gap-2">
            <ImageUpload
              value={
                values?.image && Array.isArray(values.image) && values.image[0] instanceof File
                  ? URL.createObjectURL(values.image[0])
                  : values?.thumbnail || user?.thumbnail || defaultDp
              }
              onChange={(data) => {
                if (typeof setValues === 'function') {
                  setValues((prev) => {
                    const newValues = {
                      ...(prev || {}),
                      image: Array.from(data || []),
                    };

                    return newValues;
                  });
                }
              }}
            />
            <NameEdit registerProps={{ ...register('name') }} onEditStateChange={setIsEditingName} />
            <CategoryEdit registerProps={{ ...register('categories') }} />
            <ToggleWithLabel
              className="h-[2rem]"
              name="Profile name"
              registerProps={{
                ...register('showProfileName'),
                onChange: (val) => {
                  register('showProfileName').onChange(val);
                  patchUser({ show_profile_name: val });
                },
              }}
            />
            {(isEditingName || (values?.image && Array.isArray(values.image) && values.image.length > 0)) && (
              <Button
                onClick={() => {
                  onSubmitHandler();
                  setIsEditingName(false);
                }}
                className="h-[48px] w-[150px] max-w-[470px] gap-2 whitespace-nowrap rounded-lg px-5 py-3"
                variant="primary"
                disabled={formState?.hasError}
              >
                Save Changes
              </Button>
            )}
          </div>
          <div className="form-actions flex flex-col gap-4 ">
            <span className="flex flex-wrap gap-1 text-xs text-[#475467]">
              <p>Your Linkozi page will be accessed from </p>
              <a
                href={`${import.meta.env.VITE_SITE_URL}/${user?.title || '<title>'}`}
                className="flex flex-wrap text-[#039BE5]"
              >
                {import.meta.env.VITE_SITE_URL}/{user?.title || '<title>'}
              </a>
            </span>
            {/* <img src="/src/assets/image/url.svg" alt="URL preview" className="h-auto w-full max-w-[343px]" /> */}
          </div>
        </div>

        <div className="form-action mt-6 flex w-full flex-col items-center justify-center">
          <Button onClick={handleLogOutClick} className="flex w-full gap-3" variant="primary">
            <Logout />
            <p className="font-roboto-medium text-[14px]">Log Out</p>
          </Button>
          {(formState?.isSubmitionError || isPatchUserError) && (
            <ErrorLabel
              message={
                formState?.submitionError?.response?.data?.message || 'Something is not right. Please try again.'
              }
            />
          )}
        </div>
      </div>
    </FormProvider>
  );
}
